import { useGetIdentity, useTranslate, useCustom } from "@refinedev/core";
import { flattenData } from "../../shared/utils";
import { useEffect, useState } from "react";
import { refreshToken } from "../../authProvider";
import { Alert, Spin } from "antd";
import { useTenantSettings } from "../../hooks/tenant";
import type { IUser, DashboardData, StatConfig } from "./types";
import {
  StatsGrid,
  CreditsAndInvoiceCard,
  FeaturedVehicleCard,
  RecentRequestsCard,
  MessagesCard,
} from "./components";

const DEFAULT_PRICE_PER_CREDIT = 10;

function buildStatusConfig(
  _t: (key: string, fallback?: string) => string,
): Omit<StatConfig, "count">[] {
  return [
    {
      key: "submitted",
      label: _t("orders.fields.statusValues.submitted", "Envoyées"),
      description: _t("dashboard.stats.submittedDesc", "Demandes soumises"),
      icon: "send",
      color: "#6366f1",
    },
    {
      key: "ongoing",
      label: _t("orders.fields.statusValues.ongoing", "En cours"),
      description: _t("dashboard.stats.ongoingDesc", "En traitement"),
      icon: "progress",
      color: "#3b82f6",
      pulse: true,
    },
    {
      key: "completed",
      label: _t("orders.fields.statusValues.completed", "Terminées"),
      description: _t("dashboard.stats.completedDesc", "Fichiers livrés"),
      icon: "check",
      color: "#10b981",
    },
    {
      key: "sav",
      label: _t("orders.fields.statusValues.sav", "S.A.V"),
      description: _t("dashboard.stats.savDesc", "Tickets support"),
      icon: "warning",
      color: "#f59e0b",
    },
  ];
}

function mapRecentOrders(orders: DashboardData["latestOrders"]) {
  return orders.map((order) => {
    let formFieldValues: Record<string, unknown> = {};
    if (order.payload?.trim()) {
      try {
        const formData = flattenData(JSON.parse(order.payload));
        for (let i = 0; i < formData.length; i++) {
          if (formData[i].value !== undefined)
            formFieldValues[formData[i].name] = formData[i].value;
        }
      } catch (error) {
        console.error("Error parsing order payload:", error);
      }
    }
    return { ...order, ...formFieldValues };
  });
}

export const AccountHome = () => {
  const _t = useTranslate();
  const { data: identity } = useGetIdentity<IUser>();
  const account = identity?.account?.id;
  const listUrl = `/accounts/${account}/orders/list`;

  const [isCreditModalOpen, setIsCreditModalOpen] = useState(false);
  const [hoveredStat, setHoveredStat] = useState<string | null>(null);
  const [hoveredRequest, setHoveredRequest] = useState<number | null>(null);
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState<
    number | null
  >(null);

  const tenantSettings = useTenantSettings();
  const pricePerCredit =
    tenantSettings?.pricePerCredit ?? DEFAULT_PRICE_PER_CREDIT;

  const { data: dashboardResponse, isLoading: dashboardLoading } =
    useCustom<DashboardData>({
      url: `accounts/${account}/dashboard`,
      method: "get",
      queryOptions: { enabled: !!account },
    });
  const dashboardData = dashboardResponse?.data;

  const billingMode = identity?.account?.billingMode;
  const accountCredit =
    dashboardData?.accountCredit ?? identity?.account?.credit ?? 0;
  const latestInvoice = dashboardData?.latestInvoices?.[0];

  const statusCounts: StatConfig[] = buildStatusConfig(_t).map((s) => {
    const count =
      dashboardData?.orderStatusCounts?.find((c) => c.status === s.key)
        ?.count ?? 0;
    return { ...s, count };
  });

  const recentOrders = dashboardData?.latestOrders ?? [];
  const processedRecentOrders = mapRecentOrders(recentOrders);
  const latestCompletedOrder =
    processedRecentOrders.find((o) => o.status === "completed") ??
    processedRecentOrders[0];
  const featuredOrderAttachment =
    latestCompletedOrder &&
    dashboardData?.latestOutAttachments?.find(
      (att) => att.orderId === latestCompletedOrder.id,
    );

  const announcementMessage = dashboardData?.announcementMessage?.trim() ?? "";

  useEffect(() => {
    refreshToken();
  }, []);

  if (dashboardLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="p-2 sm:p-6 max-w-7xl mx-auto">
        <div className="px-2 py-4 sm:px-4 md:px-8 md:py-8 lg:px-10 relative max-w-[1600px] mx-auto">
          {announcementMessage.length > 0 && (
            <Alert
              type="info"
              showIcon
              className="mb-8 !py-5 !px-5 sm:!py-6 sm:!px-6 text-base sm:text-lg [&_.ant-alert-icon]:text-2xl sm:[&_.ant-alert-icon]:text-3xl [&_.ant-alert-message]:!text-base sm:[&_.ant-alert-message]:!text-lg [&_.ant-alert-message]:!font-medium"
              message={
                <span className="whitespace-pre-wrap leading-relaxed">
                  {announcementMessage}
                </span>
              }
            />
          )}

          <StatsGrid
            statusCounts={statusCounts}
            listUrl={listUrl}
            hoveredStat={hoveredStat}
            onHoverStat={setHoveredStat}
          />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-7">
            <CreditsAndInvoiceCard
              billingMode={billingMode}
              accountCredit={accountCredit}
              latestInvoice={latestInvoice}
              pricePerCredit={pricePerCredit}
              accountId={account!}
              isCreditModalOpen={isCreditModalOpen}
              onCreditModalOpen={setIsCreditModalOpen}
              downloadingInvoiceId={downloadingInvoiceId}
              onDownloadingInvoiceId={setDownloadingInvoiceId}
            />
            {latestCompletedOrder && (
              <FeaturedVehicleCard
                order={latestCompletedOrder}
                outAttachment={featuredOrderAttachment}
              />
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <RecentRequestsCard
              requests={processedRecentOrders}
              statusCounts={statusCounts}
              listUrl={listUrl}
              accountId={account!}
              hoveredRequest={hoveredRequest}
              onHoverRequest={setHoveredRequest}
            />
            <MessagesCard
              messages={dashboardData?.latestMessages ?? []}
              listUrl={listUrl}
              accountId={account!}
            />
        </div>
      </div>
    </div>
  );
};
